import axios from "axios";

const errorCodes = {
  badRequest: "badRequest",
  unauthorized: "unauthorized",
  forbidden: "forbidden",
  notFound: "notFound",
  serverError: "serverError",
  unexpected: "unexpected",
  invalidCredentials: "invalidCredentials",
};

class API {
  constructor(collection) {
    this.collectionUrl = `${process.env.REACT_APP_API_BASE_URL}/${collection}`;
  }

  _handleError(error) {
    if (error.response.status === 400)
      return Promise.reject(errorCodes.badRequest);
    if (error.response.status === 401)
      return Promise.reject(errorCodes.unauthorized);
    if (error.response.status === 403)
      return Promise.reject(errorCodes.forbidden);
    if (error.response.status === 404)
      return Promise.reject(errorCodes.notFound);
    if (500 <= error.response.status <= 599)
      return Promise.reject(errorCodes.serverError);
    return Promise.reject(errorCodes.unexpected);
  }

  async getMulti(
    ordering = null,
    search = null,
    limit = 100,
    offset = 0,
    extra = {}
  ) {
    const params = { limit, offset, ...extra };
    if (ordering) params.ordering = ordering;
    if (search && search.length > 0) params.search = search;
    try {
      const response = await axios.get(`${this.collectionUrl}/`, { params });
      return Promise.resolve(response.data);
    } catch (error) {
      return this._handleError(error);
    }
  }

  async getOne(id) {
    try {
      const response = await axios.get(`${this.collectionUrl}/${id}/`);
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }

  async getOneByMissionaryId(missionaryId) {
    try {
      const response = await axios.get(`${this.collectionUrl}/`, {
        params: {
          missionary: missionaryId,
        },
      });
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }

  async create(data) {
    try {
      const response = await axios.post(`${this.collectionUrl}/`, data);
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }

  async update(id, data, partial = false) {
    try {
      let response = null;
      if (partial)
        response = await axios.patch(`${this.collectionUrl}/${id}/`, data);
      else response = await axios.put(`${this.collectionUrl}/${id}/`, data);
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }

  async delete(id) {
    try {
      const response = await axios.delete(`${this.collectionUrl}/${id}/`);
      return response;
    } catch (e) {
      return this._handleError(e);
    }
  }
}

class EntriesAPI extends API {
  constructor() {
    super();
    this.collectionUrl = `${process.env.REACT_APP_API_BASE_URL}/entries`;
  }

  async check(issuedId) {
    try {
      const response = await axios.post(`${this.collectionUrl}/check/`, {
        issuedId,
      });
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }
}

class MeetingsAPI extends API {
  constructor() {
    super();
    this.collectionUrl = `${process.env.REACT_APP_API_BASE_URL}/meetings`;
  }

  async check(meetingId, issuedId) {
    try {
      const response = await axios.post(
        `${this.collectionUrl}/entries/check/`,
        {
          meeting: meetingId,
          missionary: issuedId,
        }
      );
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }
}

class UserAPI extends API {
  constructor() {
    super();
    this.collectionUrl = `${process.env.REACT_APP_API_BASE_URL}/accounts`;
  }

  async getToken(username, password) {
    try {
      const response = await axios.post(`${this.collectionUrl}/login/`, {
        username,
        password,
      });
      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(errorCodes.invalidCredentials);
    }
  }

  async changePassword(id, newPassword) {
    try {
      const response = await axios.post(
        `${this.collectionUrl}/${id}/change-password/`,
        { password: newPassword }
      );
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }
}

class PaymentsAPI {
  constructor() {
    this.collectionUrl = `${process.env.REACT_APP_API_BASE_URL}/payments`;
  }

  async create(data) {
    try {
      const response = await axios.post(`${this.collectionUrl}/`, data);
      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(e);
    }
  }
}

const api = {
  towns: new API("towns"),
  entries: new EntriesAPI(),
  missionaries: new API("missionaries"),
  conditions: new API("conditions"),
  missions: new API("missions"),
  meetings: new MeetingsAPI(),
  meetingAttendances: new API("meetingAttendances"),
  payments: new PaymentsAPI(),
  users: new UserAPI(),
  transactions: new API("accounts/collectors"),
  entryList: new API("entry-list"),
  transfer: new API("entry-list/transfer"),
};

export default api;
export { errorCodes, API };
